/**
 * Upload Schema — V2 Conditions
 *
 * Maps to SchemaV2ConditionPost in bldt-ui-fn/v2Conditions/schemas.ts
 * POST required: name + at least one parent ID
 * - base type → workPlanId only
 * - payment type → workPlanId + activityId + contractId
 */

import { UploadSchema } from '../types';

export const v2ConditionSchema: UploadSchema = {
	entityType: 'v2Condition',
	label: 'Conditions',
	apiEndpoint: '/v2/conditions',
	apiMethod: 'POST',
	customFieldEntityType: 'condition',
	fields: [
		{
			name: 'name',
			label: 'Condition Name',
			dataType: 'string',
			required: true,
			constraints: { minLength: 1, maxLength: 255 },
			headerAliases: ['Condition', 'Condition Title', 'Title'],
		},
		{
			name: 'conditionType',
			label: 'Condition Type',
			dataType: 'enum',
			required: false,
			defaultValue: 'payment',
			enumValues: ['payment'] as const,
			headerAliases: ['Type', 'Cond. Type'],
		},
		{
			name: 'workPlanId',
			label: 'Work Plan',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'workPlan',
				matchField: 'name',
				idField: 'id',
				contextKey: 'workPlans',
			},
			headerAliases: ['Work Plan Name', 'WP'],
		},
		{
			name: 'activityId',
			label: 'Activity',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'activity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'activities',
			},
			headerAliases: ['Activity Name', 'Linked Activity'],
		},
		{
			name: 'contractId',
			label: 'Contract',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'contract',
				matchField: 'name',
				idField: 'id',
				contextKey: 'contracts',
			},
			headerAliases: ['Contract Name', 'Contract Number', 'Contract #'],
		},
		{
			name: 'description',
			label: 'Description',
			dataType: 'string',
			required: false,
			headerAliases: ['Details', 'Notes', 'Condition Description'],
		},
		{
			name: 'isRequired',
			label: 'Required?',
			dataType: 'boolean',
			required: false,
			defaultValue: true,
			headerAliases: ['Is Required', 'Mandatory', 'Required'],
		},
		{
			name: 'paymentAmount',
			label: 'Payment Amount',
			dataType: 'number',
			required: false,
			constraints: { min: 0 },
			headerAliases: ['Amount', 'Payment Value'],
		},
		{
			name: 'paymentPercentage',
			label: 'Payment Percentage',
			dataType: 'number',
			required: false,
			constraints: { min: 0, max: 100 },
			headerAliases: ['Payment %', 'Percentage', '% of Contract'],
		},
		{
			name: 'sequenceOrder',
			label: 'Sequence Order',
			dataType: 'number',
			required: false,
			constraints: { min: 1 },
			headerAliases: ['Order', 'Seq', 'Sequence #', 'Step'],
		},
	],
	crossFieldValidation: (row) => {
		const condType = row.conditionType;
		const hasWp = row.workPlanId != null;
		const hasAct = row.activityId != null;
		const hasCon = row.contractId != null;

		if (condType === 'payment' && (!hasWp || !hasAct || !hasCon)) {
			return 'Payment conditions require Work Plan + Activity + Contract';
		}
		if (!condType) {
			const count = (hasWp ? 1 : 0) + (hasAct ? 1 : 0) + (hasCon ? 1 : 0);
			if (count === 0) {
				return 'At least one parent (Work Plan, Activity, or Contract) is required';
			}
		}
		return null;
	},
};
